import React, { useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, BadgeCheck, PlayCircle } from "lucide-react";

// Self-hosted, compressed 720-portrait clip (was a 10 MB autoplay CDN mp4).
const VIDEO_URL = "/videos/director-message.mp4";
const POSTER_URL = "/videos/director-poster.webp";

// Portrait (9:16) director-message player shown above the Fast Admissions Desk.
// Loads nothing until tapped (poster only) so it never costs page-load bandwidth.
export default function DirectorVideoCard() {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const start = () => {
    setStarted(true);
    setMuted(false);
    setPlaying(true);
    setTimeout(() => {
      const v = ref.current;
      if (v) { v.muted = false; v.play().catch(() => {}); }
    }, 40);
  };
  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted && v.paused) { v.play(); setPlaying(true); }
  };

  return (
    <div className="mb-4" data-testid="director-video-card">
      <div className="flex items-center gap-1.5 mb-2 px-1">
        <BadgeCheck className="w-4 h-4 text-[#003399]" />
        <span className="text-[11px] font-black uppercase text-[#003399] tracking-wider">Message from our Director</span>
      </div>
      <div className="relative mx-auto w-full max-w-[280px] rounded-2xl overflow-hidden bg-black shadow-xl ring-2 border-2 border-blue-100" style={{ aspectRatio: "9 / 16" }}>
        {started ? (
          <video
            ref={ref}
            src={VIDEO_URL}
            poster={POSTER_URL}
            className="absolute inset-0 w-full h-full object-cover"
            loop
            playsInline
            preload="none"
            data-testid="director-video-el"
            onClick={togglePlay}
          />
        ) : (
          <>
            <img src={POSTER_URL} alt="Er. Santosh Yadav, Director" width="480" height="848" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <button
              type="button"
              onClick={start}
              data-testid="director-video-start"
              aria-label="Play the director's message video"
              className="absolute inset-0 z-[5] flex items-center justify-center bg-black/25 hover:bg-black/35 transition-colors"
            >
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/90 text-[#0B132B] shadow-lg">
                <PlayCircle className="w-10 h-10" />
              </span>
            </button>
          </>
        )}
        {/* controls (only once the clip has started) */}
        {started && (
          <div className="absolute bottom-0 inset-x-0 p-2 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent">
            <button type="button" onClick={togglePlay} data-testid="director-video-play" aria-label={playing ? "Pause video" : "Play video"}
              className="w-9 h-9 rounded-full bg-white/90 text-[#071E54] flex items-center justify-center hover:bg-white transition-colors shadow">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <button type="button" onClick={toggleMute} data-testid="director-video-mute" aria-label={muted ? "Unmute video" : "Mute video"}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/90 text-[#071E54] text-xs font-bold hover:bg-white transition-colors shadow">
              {muted ? <><VolumeX className="w-4 h-4" /> Tap for sound</> : <><Volume2 className="w-4 h-4" /> Sound on</>}
            </button>
          </div>
        )}
        <p className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/55 text-white text-[10px] font-bold">Er. Santosh Yadav · Director</p>
      </div>
    </div>
  );
}
